/**
 * Readable texts for billing error codes (never raw codes, never
 * "Edge Function returned a non-2xx status code").
 */
import type { TFunction } from 'i18next';

const KNOWN_CODES = new Set([
  'EA_SOLD_OUT', 'PAYMENT_ISSUE', 'TOO_MANY_UNITS', 'UPGRADE_KEEP_INTERVAL', 'SAME_PLAN',
  'ACCOUNT_PENDING_DELETION', 'HAS_LIVE_SUBSCRIPTION', 'NO_LIVE_SUBSCRIPTION', 'NO_CUSTOMER',
  'HAS_STRIPE_SUBSCRIPTION', 'PERIOD_ENDED_USE_GIFT', 'PLAN_REQUIRED_FOR_ACTIVE', 'PAYMENT_PENDING', 'TIMEOUT', 'BILLING_UNAVAILABLE', 'REACTIVATE_FIRST', 'PLAN_REQUIRED', 'UNIT_INACTIVE', 'SESSION', 'RATE_LIMITED', 'NETWORK',
]);

/**
 * Admin screens: a readable text for a known code, otherwise the server's
 * own message (admins may see it), otherwise null.
 */
export function adminLinkError(t: TFunction, err: unknown): string | null {
  const code = (err as { code?: unknown })?.code;
  if (typeof code === 'string' && KNOWN_CODES.has(code)) return billingErrorText(t, code);
  const message = err instanceof Error ? err.message : null;
  return message || null;
}

export function billingErrorText(t: TFunction, code: string | null | undefined): string {
  switch (code) {
    case 'EA_SOLD_OUT':
      return t('billing.errors.eaSoldOut', 'Sva Early Adopter mjesta su popunjena. Odaberi neki drugi plan.');
    case 'PAYMENT_ISSUE':
      return t('billing.errors.paymentIssue', 'Zadnja naplata nije uspjela. Prvo ažuriraj karticu, zatim promijeni plan.');
    case 'TOO_MANY_UNITS':
      return t('billing.errors.tooManyUnits', 'Imaš više aktivnih jedinica nego što novi plan dopušta. Prvo deaktiviraj višak.');
    case 'UPGRADE_KEEP_INTERVAL':
      return t('billing.errors.upgradeKeepInterval', 'Veći plan možeš uzeti uz godišnju naplatu. Na mjesečnu naplatu možeš prijeći na kraju godišnjeg razdoblja.');
    case 'SAME_PLAN':
      return t('billing.errors.samePlan', 'To je već tvoj plan.');
    case 'ACCOUNT_PENDING_DELETION':
      return t('billing.errors.accountPendingDeletion', 'Račun je zakazan za brisanje. Prvo poništi brisanje (Postavke računa), pa onda plati ili promijeni plan.');
    case 'HAS_LIVE_SUBSCRIPTION':
      return t('billing.errors.hasLiveSubscription', 'Korisnik već ima aktivnu Stripe pretplatu — drugi link za plaćanje naplatio bi dvaput.');
    case 'NO_LIVE_SUBSCRIPTION':
      return t('billing.errors.noLiveSubscription', 'Korisnik nema aktivnu Stripe pretplatu — pošalji mu link za plaćanje.');
    case 'NO_CUSTOMER':
      return t('billing.errors.noCustomer', 'Još nisi ništa platio karticom, pa nema što urediti. Odaberi plan ispod.');
    case 'REACTIVATE_FIRST':
      return t('billing.errors.reactivateFirst', 'Automatska obnova je isključena. Za promjenu plana prvo je uključi (Naplata → „Uključi obnovu") — ništa se ne naplaćuje.');
    case 'BILLING_UNAVAILABLE':
      return t('billing.errors.billingUnavailable', 'Naplata trenutno nije dostupna — javi se podršci na admin@bookiapp.com. Tvoj plan i podaci se ne mijenjaju.');
    case 'PAYMENT_PENDING':
      return t('billing.errors.paymentPending', 'Plaćanje čeka potvrdu. Otvori račun i potvrdi plaćanje — nakon toga možeš mijenjati plan.');
    case 'TIMEOUT':
      return t('billing.errors.timeout', 'Nismo dobili odgovor na vrijeme. Prije novog pokušaja provjeri u Naplati je li se plan već promijenio, da ne platiš dvaput.');
    case 'PLAN_REQUIRED_FOR_ACTIVE':
      return t('billing.errors.planRequiredForActive', 'Za status Aktivan odaberi plan (Starter, Pro, Business ili Early Adopter).');
    case 'PERIOD_ENDED_USE_GIFT':
      return t('billing.errors.periodEndedUseGift', "Plan je istekao — koristi 'Pokloni' i odaberi trajanje.");
    case 'HAS_STRIPE_SUBSCRIPTION':
      return t('billing.errors.hasStripe', 'Ovaj korisnik plaća preko Stripea. Pretplatom se upravlja u Stripeu (ili je korisnik sam otkaže) — ručna promjena bi se prepisala.');
    case 'PLAN_REQUIRED':
      return t('billing.errors.planRequired', 'Tvoj plan je istekao, pa ne možeš dodavati nove rezervacije, objekte ni jedinice. Odaberi plan u Naplati.');
    case 'UNIT_INACTIVE':
      return t('billing.errors.unitInactive', 'Ova jedinica je deaktivirana, pa ne prima nove rezervacije. Aktiviraj je ili odaberi drugu jedinicu.');
    case 'SESSION':
      return t('billing.errors.session', 'Prijava je istekla. Osvježi stranicu i pokušaj ponovno.');
    case 'RATE_LIMITED':
      return t('billing.errors.rateLimited', 'Previše pokušaja zaredom. Pričekaj minutu pa pokušaj ponovno.');
    case 'NETWORK':
      return t('billing.errors.network', 'Nema veze s poslužiteljem. Provjeri internet i pokušaj ponovno.');
    default:
      return t('billing.errors.generic', 'Nešto nije uspjelo. Pokušaj ponovno za minutu ili nam se javi na admin@bookiapp.com.');
  }
}
